import { DestroyRef, ElementRef, forwardRef, inject, TemplateRef } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeWhileAlive } from '@core/frontend-shared/utils';
import { convertToSubject } from '@core/shared/utils';
import { filter, take } from 'rxjs';
import { ModelBasedControlValueAccessor } from '../model-based-control-value-accessor';
import { ArrayFormService } from './array-form.service';
import { AsyncPipe } from '@angular/common';
import { UiButtonComponent } from '@common/ui/frontend-shared/button';
import { UiContentCenteredComponent } from '@common/ui/frontend-shared/content-centered';
import { UiSpinnerComponent } from '@common/ui/frontend-shared/spinner';
import { ArrayFieldsetInner } from './array-fieldset-inner';
import * as i0 from "@angular/core";
import * as i1 from "@common/ui/frontend-shared/orderlist";
import * as i2 from "@angular/forms";
function ArrayFieldset_Conditional_0_ng_template_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "array-fieldset-inner", 4);
    i0.ɵɵlistener("removeItem", function ArrayFieldset_Conditional_0_ng_template_2_Template_array_fieldset_inner_removeItem_0_listener() {
      const index_r4 = i0.ɵɵrestoreView(_r3).index;
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.formService.removeItem(index_r4));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const formControl_r5 = ctx.$implicit;
    const index_r4 = ctx.index;
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("Model", ctx_r1.Model)("formGroup", formControl_r5)("formService", ctx_r1.formService)("innerForm", ctx_r1.innerForm)("index", index_r4);
  }
}
function ArrayFieldset_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "ui-orderList", 0);
    i0.ɵɵpipe(1, "async");
    i0.ɵɵlistener("onSelectionChange", function ArrayFieldset_Conditional_0_Template_ui_orderList_onSelectionChange_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.updateSelection($event));
    })("onReorderItems", function ArrayFieldset_Conditional_0_Template_ui_orderList_onReorderItems_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onReorderItems($event));
    });
    i0.ɵɵtemplate(2, ArrayFieldset_Conditional_0_ng_template_2_Template, 1, 5, "ng-template", 1);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "div", 2)(4, "button", 3);
    i0.ɵɵlistener("click", function ArrayFieldset_Conditional_0_Template_button_click_4_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.formService.addNewItem());
    });
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("value", i0.ɵɵpipeBind1(1, 7, ctx_r1.controls$))("header", ctx_r1.title)("selectable", ctx_r1.enableSelection)("dragdrop", ctx_r1.reordering)("selection", ctx_r1.selectedItems)("metaKeySelection", ctx_r1.enableSelection);
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("label", ctx_r1.buttonLabel);
  }
}
function ArrayFieldset_Conditional_1_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "small", 5);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵelement(3, "br");
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" fieldset initialized: ", i0.ɵɵpipeBind1(2, 2, ctx_r1.initialized$));
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" formService initialized: ", ctx_r1.formService ? ctx_r1.formService.initialized : "unknown", " ");
  }
}
function ArrayFieldset_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "ui-content-centered")(1, "div");
    i0.ɵɵelement(2, "ui-spinner");
    i0.ɵɵtext(3, " Editor wird initialisiert... ");
    i0.ɵɵconditionalCreate(4, ArrayFieldset_Conditional_1_Conditional_4_Template, 5, 4, "small", 5);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(4);
    i0.ɵɵconditional(ctx_r1.isDev ? 4 : -1);
  }
}
export const ARRAY_FIELDSET_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ArrayFieldset),
  multi: true
};
export const ARRAY_FIELDSET_VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => ArrayFieldset),
  multi: true
};
export let ArrayFieldset = /*#__PURE__*/(() => {
  class ArrayFieldset extends ModelBasedControlValueAccessor {
    constructor() {
      super(...arguments);
      this.destroyRef = inject(DestroyRef);
      this.value = [];
      this.reordering = false;
      this.enableSelection = false;
      this.buttonLabel = "Item hinzufügen";
      this.innerFormData = {};
      this.formServiceSubscriber$ = this.formService$.pipe(filter(fs => !!fs), takeWhileAlive()).subscribe(service => {
        this.bindFormServiceObservables();
        if (this.debug) service.debug();
      });
    }
    set controlFormServiceChildProp(info) {
      this.parentFormService = info[0];
      this.useParentFormServiceFormControl = info[1];
    }
    _tryToInitialize() {
      if (this.initialized) return;
      const canSetupUsingFormService = !!this.formService;
      const canSetupUsingParentFormServiceFormControl = this.parentFormService && this.useParentFormServiceFormControl;
      if (canSetupUsingFormService) {
        // explicit model is optional, formService should already have it set
        if (this.Model) this.formService.setModel(this.Model);
        if (this.debug) console.log('[DEBUG] ModelBasedCVA: initialize canSetupUsingFormService');
        // wait for formService to notify of initialization
        this.formService.initialized$.pipe(filter(value => !!value), take(1)).subscribe(() => {
          this._value = this.formService.getValue();
          this._executeInitialization();
        });
        if (!IS_PRODUCTION) {
          setTimeout(() => {
            if (!this.formService.initialized) {
              console.warn('ArrayFieldset detected an issue: assigned formService has not been initialized. Passed FormServices are not auto-initialized anymore! Make sure to intialize them manually.', this.formService);
            }
          }, 2000);
        }
      } else if (canSetupUsingParentFormServiceFormControl) {
        // console.log('[DEBUG] ArrayFieldset: initialize canSetupUsingParentFormServiceFormControl')
        if (this.debug) console.log('[DEBUG] ArrayFieldset: initialize canSetupUsingParentFormServiceFormControl');
        const service = this.parentFormService;
        const expectArray = service.dataIsArray();
        let formArray;
        if (expectArray) {
          throw new Error('directly nested array-fieldsets are unsupported');
          // parent is formArray, controlName is array index, all use same model
          // const controlName = this.useParentFormServiceFormControl as number;
          // this.Model = service.modelFactory.Model;
          // formGroup = (this.parentFormService.formGroup as FormArray).controls[controlName] as any;
        } else {
          // parent is formGroup, controlName is formGroup key, load nested model
          const controlName = this.useParentFormServiceFormControl;
          const propInfo = this.parentFormService.getModelPropTypeInfo(controlName);
          if (!propInfo) throw Error('Initialize using ParentFormServiceFormControl failed: Prop ' + controlName + ' has no type info');
          this.Model = propInfo.Model;
          formArray = this.parentFormService.formGroup.get(controlName);
        }
        if (!(formArray instanceof UntypedFormArray)) throw new Error('control to be used for subform is no FormArray! ' + this.useParentFormServiceFormControl + ': ' + formArray);
        this.createDefaultFormService();
        // assign Model and initial data (which is already set within formGroup that was created by parent form)
        this.formService.setModel(this.Model);
        this.formService.setFormArray(formArray);
        this.controls$ = this.formService.formGroup.controls$.pipe(convertToSubject([]));
        this._executeInitialization({
          initializeFormService: true
        });
      }
    }
    createDefaultFormService() {
      this.formService$.next(this.formServiceFactory.useDestroyRef(this.destroyRef).createUninitialized(ArrayFormService));
      if (this.debug) this.formService.debug();
    }
    onReorderItems(event) {
      this.formService.reorderItems(event.actions);
    }
    updateSelection($event) {}
    static {
      this.ɵfac = /*@__PURE__*/(() => {
        let ɵArrayFieldset_BaseFactory;
        return function ArrayFieldset_Factory(__ngFactoryType__) {
          return (ɵArrayFieldset_BaseFactory || (ɵArrayFieldset_BaseFactory = i0.ɵɵgetInheritedFactory(ArrayFieldset)))(__ngFactoryType__ || ArrayFieldset);
        };
      })();
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: ArrayFieldset,
        selectors: [["array-fieldset"]],
        inputs: {
          reordering: "reordering",
          enableSelection: "enableSelection",
          buttonLabel: "buttonLabel",
          title: "title",
          innerForm: "innerForm",
          innerFormData: "innerFormData",
          controlFormServiceChildProp: "controlFormServiceChildProp"
        },
        features: [i0.ɵɵProvidersFeature([ARRAY_FIELDSET_VALUE_ACCESSOR, ARRAY_FIELDSET_VALIDATOR]), i0.ɵɵInheritDefinitionFeature],
        decls: 2,
        vars: 1,
        consts: [["selectionLimit", "enableSelection ? 1 : 0", "dragHandlePosition", "left", 3, "onSelectionChange", "onReorderItems", "value", "header", "selectable", "dragdrop", "selection", "metaKeySelection"], ["uiTemplate", "item"], [1, "new-item"], ["icon", "plus", "uiButton", "", 1, "ui-button-grey", "button-add", "small", 3, "click", "label"], [3, "removeItem", "Model", "formGroup", "formService", "innerForm", "index"], [2, "display", "block", "opacity", ".5"]],
        template: function ArrayFieldset_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵconditionalCreate(0, ArrayFieldset_Conditional_0_Template, 5, 9)(1, ArrayFieldset_Conditional_1_Template, 5, 1, "ui-content-centered");
          }
          if (rf & 2) {
            i0.ɵɵconditional(ctx.initialized && (ctx.formService == null ? null : ctx.formService.formGroup) ? 0 : 1);
          }
        },
        dependencies: [i1.UiOrderListComponent, i1.UiTemplateDirective, ArrayFieldsetInner, FormsModule, i2.NgControlStatusGroup, ReactiveFormsModule, i2.FormGroupDirective, UiButtonComponent, UiContentCenteredComponent, UiSpinnerComponent, AsyncPipe],
        styles: ["[_nghost-%COMP%]    >ui-orderList>.p-orderlist>.p-orderlist-list-container>.p-orderlist-list{border:0!important;box-sizing:border-box;height:auto;overflow:unset}[_nghost-%COMP%]    >ui-orderList>.p-orderlist>.p-orderlist-list-container>.p-orderlist-list>.p-orderlist-item{padding-right:45px!important}.new-item[_ngcontent-%COMP%]{text-align:center;background:var(--overlaySoftColor);margin-bottom:6px}.new-item[_ngcontent-%COMP%]   button[_ngcontent-%COMP%]{background:transparent;border:none}"],
        changeDetection: 0
      });
    }
  }
  return ArrayFieldset;
})();